import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useProvider } from '../../contexts/ProviderContext';
import { useToast } from '../../contexts/ToastContext';
import { useProviderProducts } from '../../hooks/useProviderProducts';
import { useProviderPricePositions } from '../../hooks/useProviderPricePositions';
import { useAutoTags } from '../../hooks/useAutoTags';
import { useProviderSelection } from '../../hooks/useProviderSelection';
import { useRemovedProducts } from '../../hooks/useRemovedProducts';
import { useAuth } from '../../contexts/AuthContext';
import { useShop } from '../../contexts/ShopContext';
import { supabase } from '../../lib/supabase';
import ProductCard from './ProductCard';
import ProductListItem from './ProductListItem';
import ProductEditModal from './ProductEditModal';
import ProviderLayout from './ProviderLayout';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  PlusIcon,
  ListBulletIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const ProviderCatalog: React.FC = () => {
  const { user } = useAuth();
  const { currentProviderId } = useProvider();
  const { currentShopId } = useShop();
  const [searchParams] = useSearchParams();
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const queryProviderId = searchParams.get('providerId');

  // Priority: URL param > query param > context providerId
  // URL param is used for merchant-facing catalog route (/providers/:id/catalog)
  // Query param is used for legacy provider route (/provider/catalog?providerId=...)
  // Context is used for provider's own catalog (/provider/catalog)
  const effectiveProviderId = urlProviderId || queryProviderId || currentProviderId;

  const toast = useToast();

  const {
    products,
    loading,
    error: hookError,
    filters,
    updateFilters,
    resetFilters,
    pagination,
    setPage,
    setItemsPerPage,
    allCategories,
    allVendors,
    refresh,
  } = useProviderProducts(effectiveProviderId);

  const [showFilters, setShowFilters] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [viewType, setViewType] = useState<'grid' | 'list'>('grid');
  const [showModeConfirm, setShowModeConfirm] = useState(false);

  // Per-provider selection (only active for merchants viewing a provider catalog)
  const isMerchantView = Boolean(urlProviderId && currentShopId);
  const selection = useProviderSelection(
    isMerchantView ? currentShopId : null,
    isMerchantView ? effectiveProviderId : null,
  );

  // Merchant-wide "removed from my store" list. Distinct from `selection` above, which is
  // per-supplier sourcing.
  const removedProducts = useRemovedProducts(isMerchantView ? currentShopId : null);

  // "Are you the lowest price?" is for a supplier on its own catalogue, never for a merchant
  // browsing one.
  const pricePositions = useProviderPricePositions(isMerchantView ? null : effectiveProviderId, products);
  // What the merchant's store tags each product with automatically, before any sync (W5).
  const autoTags = useAutoTags(isMerchantView ? currentShopId : null, products);
  const [pendingRemoval, setPendingRemoval] = useState<{ sku: string; title: string } | null>(null);

  const handleRemoveRequest = (sku: string) => {
    const product = products.find((p) => p.sku === sku);
    setPendingRemoval({ sku, title: product?.title || sku });
  };

  const confirmRemoval = async () => {
    if (!pendingRemoval) return;
    const ok = await removedProducts.removeProduct(pendingRemoval.sku);
    toast[ok ? 'success' : 'error'](
      ok
        ? `Removed ${pendingRemoval.sku} from your store and set its stock to 0`
        : `Could not remove ${pendingRemoval.sku}`,
    );
    setPendingRemoval(null);
  };

  const handleRestore = async (sku: string) => {
    const ok = await removedProducts.restoreProduct(sku);
    toast[ok ? 'success' : 'error'](
      ok ? `Restored ${sku}. Stock returns on the next sync.` : `Could not restore ${sku}`,
    );
  };

  // Check if user has edit permissions via auth_users_providers
  useEffect(() => {
    const checkEditPermissions = async () => {
      if (!user?.id || !effectiveProviderId) {
        setCheckingAuth(false);
        return;
      }

      try {
        // Check if user has auth_users_providers entry for this provider
        const { data, error } = await supabase
          .from('auth_users_providers')
          .select('provider_id')
          .eq('auth_user_id', user.id)
          .eq('provider_id', effectiveProviderId)
          .single();

        if (error || !data) {
          // No auth_users_providers entry = read-only mode
          setReadOnlyMode(true);
        } else {
          // Has entry = can edit
          setReadOnlyMode(false);
        }
      } catch (err) {
        console.error('Error checking edit permissions:', err);
        setReadOnlyMode(true); // Default to read-only on error
      } finally {
        setCheckingAuth(false);
      }
    };

    checkEditPermissions();
  }, [user?.id, effectiveProviderId]);

  // Show toast when hook error changes
  useEffect(() => {
    if (hookError) {
      toast.error(hookError);
    }
  }, [hookError]);

  // Handle view type persistence and smart defaulting
  useEffect(() => {
    // Only run when products are loaded
    if (loading || products.length === 0) return;

    // Check localStorage for saved preference
    const savedView = localStorage.getItem('provider-catalog-view') as 'grid' | 'list' | null;

    if (savedView) {
      setViewType(savedView);
    } else {
      // Smart default: if more than 50% of products lack images, default to list view
      const productsWithoutImages = products.filter(p => !p.photo_url || p.photo_url.trim() === '').length;
      const percentageWithoutImages = (productsWithoutImages / products.length) * 100;

      if (percentageWithoutImages > 50) {
        setViewType('list');
        localStorage.setItem('provider-catalog-view', 'list');
      }
    }
  }, [products, loading]);

  // Save view type to localStorage when it changes
  const handleViewTypeChange = (newView: 'grid' | 'list') => {
    setViewType(newView);
    localStorage.setItem('provider-catalog-view', newView);
  };

  const handleEditProduct = (productId: string) => {
    setSelectedProduct(productId);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedProduct(null);
  };

  const handleSaveProduct = () => {
    // Refresh the product list
    refresh();
  };

  // Detect whether any filters are active
  const hasActiveFilters = Boolean(
    filters.searchTerm || filters.category || filters.vendor || filters.stockStatus !== 'all',
  );

  // Visible product SKUs (current page)
  const visibleSkus = products.map((p) => p.sku);

  // How many visible products are currently selected/deselected
  const visibleSelectedCount = products.filter((p) => selection.isSelected(p.sku)).length;
  const visibleDeselectedCount = products.length - visibleSelectedCount;

  // Page number array for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let end = Math.min(pagination.totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <ProviderLayout>
      {/* Mode Switch Confirmation Dialog */}
      {showModeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="glass-card rounded-xl p-6 max-w-sm mx-4 space-y-4">
            <h3 className="text-white font-semibold text-lg">Switch Selection Mode?</h3>
            <p className="text-white/70 text-sm">
              Switching modes will clear your current selection ({selection.selectionCount}{' '}
              {selection.selectionMode === 'select_all' ? 'skipped items' : 'selected items'}).
              This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowModeConfirm(false)}
                className="px-4 py-2 text-sm text-white/70 border border-white/15 rounded hover:bg-white/[0.08]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  selection.toggleMode();
                  setShowModeConfirm(false);
                }}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
              >
                Switch Mode
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove-from-store confirmation. Worth a confirm because it applies to EVERY
          supplier and zeroes the product's stock immediately, unlike the per-supplier
          checkbox next to it. */}
      {pendingRemoval && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="glass-card rounded-xl p-6 max-w-md mx-4 space-y-4">
            <h3 className="text-white font-semibold text-lg">Remove from your store?</h3>
            <p className="text-white/70 text-sm">
              <span className="text-white">{pendingRemoval.title}</span>{' '}
              <span className="text-white/50">({pendingRemoval.sku})</span>
            </p>
            <ul className="text-white/60 text-sm space-y-1 list-disc pl-5">
              <li>Its stock is set to 0 straight away, so it stops selling.</li>
              <li>FlowStock will not re-upload it, from any supplier.</li>
              <li>You can restore it here at any time.</li>
            </ul>
            <p className="text-white/40 text-xs">
              This is not the same as unchecking a product for one supplier — that only
              stops sourcing it from that supplier and keeps it on your store.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPendingRemoval(null)}
                disabled={removedProducts.saving}
                className="px-4 py-2 text-sm text-white/70 border border-white/15 rounded hover:bg-white/[0.08] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoval}
                disabled={removedProducts.saving}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {removedProducts.saving ? 'Removing…' : 'Remove from store'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <ProductEditModal
          productId={selectedProduct}
          providerId={effectiveProviderId}
          onClose={handleCloseModal}
          onSave={handleSaveProduct}
          readOnly={readOnlyMode}
          pricePosition={selectedProduct ? pricePositions[selectedProduct] : undefined}
        />
      )}

          {/* Search + Filter Bar */}
          <div className="mb-5 space-y-3">
            {/* Row 1: Search + View Toggle + Actions */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              {/* Search */}
              <div className="relative flex-1 max-w-xl">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  placeholder="Search products, SKUs, vendors..."
                  className="w-full pl-9 pr-4 py-2 bg-white/[0.04] border border-white/10 rounded-lg text-white text-sm placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-orange-500/50 focus:border-orange-500/50 transition-colors"
                  value={filters.searchTerm}
                  onChange={(e) => updateFilters({ searchTerm: e.target.value })}
                />
                {filters.searchTerm && (
                  <button
                    onClick={() => updateFilters({ searchTerm: '' })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/30 hover:text-white/60 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* View Toggle */}
                <div className="flex bg-white/[0.04] border border-white/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => handleViewTypeChange('grid')}
                    className={`p-2 transition-colors ${
                      viewType === 'grid'
                        ? 'bg-orange-600 text-white'
                        : 'text-white/50 hover:text-white/80 hover:bg-white/[0.06]'
                    }`}
                    title="Grid view"
                  >
                    <Squares2X2Icon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleViewTypeChange('list')}
                    className={`p-2 transition-colors ${
                      viewType === 'list'
                        ? 'bg-orange-600 text-white'
                        : 'text-white/50 hover:text-white/80 hover:bg-white/[0.06]'
                    }`}
                    title="List view"
                  >
                    <ListBulletIcon className="w-4 h-4" />
                  </button>
                </div>

                {!readOnlyMode && !isMerchantView && (
                  <button
                    onClick={() => window.location.href = '/provider/catalog/new'}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">New Product</span>
                  </button>
                )}
                {readOnlyMode && !isMerchantView && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-lg text-xs">
                    Read-Only
                  </span>
                )}
              </div>
            </div>

            {/* Row 2: Filters + Selection Controls */}
            <div className="flex flex-col lg:flex-row gap-3 justify-between items-start lg:items-center">
              <div className="flex flex-wrap items-center gap-2">
                {/* Filter dropdowns */}
                <select
                  value={filters.category}
                  onChange={(e) => updateFilters({ category: e.target.value })}
                  className={`px-3 py-1.5 bg-white/[0.04] border rounded-lg text-sm transition-colors ${
                    filters.category
                      ? 'border-orange-500/40 text-orange-300'
                      : 'border-white/10 text-white/60'
                  }`}
                >
                  <option value="">Category</option>
                  {allCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                <select
                  value={filters.vendor}
                  onChange={(e) => updateFilters({ vendor: e.target.value })}
                  className={`px-3 py-1.5 bg-white/[0.04] border rounded-lg text-sm transition-colors ${
                    filters.vendor
                      ? 'border-orange-500/40 text-orange-300'
                      : 'border-white/10 text-white/60'
                  }`}
                >
                  <option value="">Vendor</option>
                  {allVendors.map((vendor) => (
                    <option key={vendor} value={vendor}>{vendor}</option>
                  ))}
                </select>

                <select
                  value={filters.stockStatus}
                  onChange={(e) => updateFilters({ stockStatus: e.target.value as any })}
                  className={`px-3 py-1.5 bg-white/[0.04] border rounded-lg text-sm transition-colors ${
                    filters.stockStatus !== 'all'
                      ? 'border-orange-500/40 text-orange-300'
                      : 'border-white/10 text-white/60'
                  }`}
                >
                  <option value="all">Stock</option>
                  <option value="in_stock">In Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>

                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                    Clear
                  </button>
                )}

                {/* Separator */}
                {isMerchantView && selection.loaded && (
                  <div className="hidden sm:block w-px h-5 bg-white/10 mx-1" />
                )}

                {/* Selection controls (merchant view only) */}
                {isMerchantView && selection.loaded && (
                  <>
                    {/* Mode indicator */}
                    <div className="flex bg-white/[0.04] border border-white/10 rounded-lg overflow-hidden">
                      <button
                        onClick={() => {
                          if (selection.selectionMode !== 'select_all') {
                            if (selection.selectionCount > 0) {
                              setShowModeConfirm(true);
                            } else {
                              selection.toggleMode();
                            }
                          }
                        }}
                        className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                          selection.selectionMode === 'select_all'
                            ? 'bg-orange-600/90 text-white'
                            : 'text-white/40 hover:text-white/70 hover:bg-white/[0.06]'
                        }`}
                        title="Sourcing every product from this supplier. Unchecking one stops sourcing it HERE only — it stays on your store if another supplier has it."
                      >
                        Source all
                      </button>
                      <button
                        onClick={() => {
                          if (selection.selectionMode !== 'deselect_all') {
                            if (selection.selectionCount > 0) {
                              setShowModeConfirm(true);
                            } else {
                              selection.toggleMode();
                            }
                          }
                        }}
                        className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                          selection.selectionMode === 'deselect_all'
                            ? 'bg-orange-600/90 text-white'
                            : 'text-white/40 hover:text-white/70 hover:bg-white/[0.06]'
                        }`}
                        title="Sourcing nothing from this supplier. Check a product to start sourcing it from here."
                      >
                        Source none
                      </button>
                    </div>

                    {/* Batch actions for visible products */}
                    {products.length > 0 && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => selection.selectProducts(visibleSkus)}
                          disabled={visibleDeselectedCount === 0}
                          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 rounded-md transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-orange-400"
                          title={hasActiveFilters ? 'Select all filtered products' : 'Select all visible products'}
                        >
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          {hasActiveFilters ? 'Select filtered' : 'Select page'}
                        </button>
                        <button
                          onClick={() => selection.deselectProducts(visibleSkus)}
                          disabled={visibleSelectedCount === 0}
                          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-red-400"
                          title={hasActiveFilters ? 'Deselect all filtered products' : 'Deselect all visible products'}
                        >
                          <XCircleIcon className="w-3.5 h-3.5" />
                          {hasActiveFilters ? 'Deselect filtered' : 'Deselect page'}
                        </button>
                      </div>
                    )}

                    {/* Status */}
                    {(selection.selectionCount > 0 || selection.saving || selection.saveError) && (
                      <span className="text-xs text-white/35">
                        {selection.saveError ? (
                          <span className="text-red-400">Save failed</span>
                        ) : selection.saving ? (
                          <span className="text-orange-400 animate-pulse">Saving...</span>
                        ) : (
                          selection.selectionMode === 'select_all'
                            ? `${selection.selectionCount} skipped`
                            : `${selection.selectionCount} selected`
                        )}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Pagination Controls (Desktop) */}
              <div className="hidden lg:flex items-center gap-2">
                <select
                  value={pagination.itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="px-2 py-1.5 bg-white/[0.04] border border-white/10 text-white/60 rounded-lg text-xs"
                >
                  <option value={24}>24/page</option>
                  <option value={48}>48/page</option>
                  <option value={96}>96/page</option>
                </select>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="p-1.5 border border-white/10 text-white/50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.06] transition-colors"
                  >
                    <ChevronLeftIcon className="w-3.5 h-3.5" />
                  </button>

                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`min-w-[32px] px-2 py-1.5 border rounded-lg text-xs transition-colors ${
                        pageNum === pagination.page
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'text-white/50 border-white/10 hover:bg-white/[0.06]'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-1.5 border border-white/10 text-white/50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.06] transition-colors"
                  >
                    <ChevronRightIcon className="w-3.5 h-3.5" />
                  </button>
                </div>

                <span className="text-xs text-white/35">
                  {pagination.totalItems} items
                </span>
              </div>
            </div>
          </div>

          {/* Mobile Pagination (Sticky) */}
          <div className="lg:hidden sticky top-16 z-30 bg-black/80 backdrop-blur-sm border-y border-white/[0.06] py-2.5 mb-4 -mx-6 px-6">
            <div className="flex items-center justify-between gap-2">
              <select
                value={pagination.itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-2 py-1 bg-white/[0.04] border border-white/10 text-white/60 rounded-lg text-xs"
              >
                <option value={24}>24/pg</option>
                <option value={48}>48/pg</option>
                <option value={96}>96/pg</option>
              </select>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-1 border border-white/10 text-white/50 rounded-lg disabled:opacity-30"
                >
                  <ChevronLeftIcon className="w-3.5 h-3.5" />
                </button>

                <span className="px-2 py-1 text-xs text-white/50">
                  {pagination.page}/{pagination.totalPages}
                </span>

                <button
                  onClick={() => setPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="p-1 border border-white/10 text-white/50 rounded-lg disabled:opacity-30"
                >
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>

              <span className="text-xs text-white/35">
                {pagination.totalItems}
              </span>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-white/50  text-sm">Loading products...</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !hookError && products.length === 0 && (
            <div className="glass-card rounded-xl p-12 text-center">
              <p className="text-white/50 text-sm">No products found.</p>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-full text-sm hover:bg-orange-400 transition-colors font-semibold"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}

          {/* Product Grid or List */}
          {!loading && !hookError && products.length > 0 && (
            <>
              {viewType === 'grid' ? (
                <div
                  className="grid gap-6 pb-6"
                  style={{
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  }}
                >
                  {products.map((product) => (
                    <ProductCard
                      key={product.product_id}
                      product={product}
                      onEdit={handleEditProduct}
                      selectable={isMerchantView && selection.loaded}
                      selected={isMerchantView ? selection.isSelected(product.sku) : undefined}
                      onToggleSelect={isMerchantView ? selection.toggleProduct : undefined}
                      removed={isMerchantView ? removedProducts.isRemoved(product.sku) : undefined}
                      onRemove={isMerchantView ? handleRemoveRequest : undefined}
                      onRestore={isMerchantView ? handleRestore : undefined}
                      pricePosition={pricePositions[product.product_id]}
                      autoTags={autoTags.tags[product.product_id]}
                      autoTagsActive={autoTags.enabled}
                    />
                  ))}
                </div>
              ) : (
                <div className="glass-card rounded-xl rounded overflow-hidden pb-6">
                  {/* List Header - Desktop Only */}
                  <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-transparent border-b border-white/[0.08]  text-xs font-semibold text-white/70">
                    {isMerchantView && selection.loaded && <div className="w-5"></div>}
                    <div className="w-2"></div>
                    <div className="flex-1 grid grid-cols-12 gap-3">
                      <div className="col-span-3">PRODUCT</div>
                      <div className="col-span-2">SKU</div>
                      <div className="col-span-2">VENDOR</div>
                      <div className="col-span-2">CATEGORY</div>
                      <div className="col-span-1 text-center">VARS</div>
                      <div className="col-span-1 text-right">PRICE</div>
                      <div className="col-span-1"></div>
                    </div>
                  </div>

                  {/* List Items */}
                  {products.map((product) => (
                    <ProductListItem
                      key={product.product_id}
                      product={product}
                      onEdit={handleEditProduct}
                      selectable={isMerchantView && selection.loaded}
                      selected={isMerchantView ? selection.isSelected(product.sku) : undefined}
                      onToggleSelect={isMerchantView ? selection.toggleProduct : undefined}
                      removed={isMerchantView ? removedProducts.isRemoved(product.sku) : undefined}
                      onRemove={isMerchantView ? handleRemoveRequest : undefined}
                      onRestore={isMerchantView ? handleRestore : undefined}
                      pricePosition={pricePositions[product.product_id]}
                      autoTags={autoTags.tags[product.product_id]}
                      autoTagsActive={autoTags.enabled}
                    />
                  ))}
                </div>
              )}
            </>
          )}
    </ProviderLayout>
  );
};

export default ProviderCatalog;
